/**
 * 🎓 BLOC OBJECTIFS PÉDAGOGIQUES - GOUVERNANCE OWEN
 * ================================================
 * 
 * Composant LECTURE SEULE affichant la progression pédagogique.
 * L'utilisateur ne peut RIEN modifier - seul Owen valide.
 * 
 * Design Premium - Qualiopi-ready
 */

import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { 
  Target, Award, CheckCircle2, Circle, Lock, 
  Sparkles, TrendingUp, Brain, Shield,
  ChevronDown, ChevronRight, AlertCircle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Progress } from './ui/progress';
import { Badge } from './ui/badge';
import { getBackendUrl } from '../config/environment';
import { subscribeToRefresh, subscribeToEvaluation } from '../utils/pedagogicalEvents';

const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

/**
 * Bloc Objectifs Pédagogiques Premium
 * LECTURE SEULE - Aucune interaction utilisateur
 */
const PedagogicalObjectives = ({ moduleId, moduleOrder }) => {
  const [progressData, setProgressData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // 🔄 Fonction de rechargement des données (memoized)
  // 🔒 P0-J FIX: Ajout cache-bust et logs détaillés pour traçabilité
  const fetchProgressData = useCallback(async (showLoader = false) => {
    try {
      if (showLoader) setLoading(true);
      const timestamp = Date.now();
      console.log(`[PedagogicalObjectives] 🔄 [${timestamp}] Rechargement progression module ${moduleId}...`);
      
      const token = localStorage.getItem('token') || localStorage.getItem('access_token');
      // 🔒 P0-J FIX: Cache-bust pour forcer données fraîches
      const response = await axios.get(`${API}/pedagogical/progress/${moduleId}?_t=${timestamp}`, {
        headers: token ? { 
          'Authorization': `Bearer ${token}`,
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        } : {}
      });
      
      // 🔒 P0-J AUDIT: Log détaillé de CHAQUE objectif pour traçabilité
      console.log(`[PedagogicalObjectives] 🔒 P0-J AUDIT - Module ${moduleId}:`);
      if (response.data?.objectives_progress) {
        response.data.objectives_progress.forEach(obj => {
          console.log(`  📊 ${obj.objective_id}: ${obj.title?.substring(0, 40)}... → ${obj.progress_percentage}% (${obj.evidence_count}/${obj.min_evidence_count} preuves)`);
        });
      }
      
      setProgressData(response.data);
      setError(null);
      console.log(`[PedagogicalObjectives] ✅ [${timestamp}] Données rechargées avec succès`);
    } catch (err) {
      console.error('Erreur chargement progression pédagogique:', err);
      setError('Impossible de charger la progression');
    } finally {
      setLoading(false);
    }
  }, [moduleId]);

  // 🎯 Chargement initial
  useEffect(() => {
    fetchProgressData(true);
  }, [fetchProgressData]);

  // 🔔 Écouter les événements de refresh (évaluation Owen terminée)
  useEffect(() => {
    console.log(`[PedagogicalObjectives] 👂 Abonnement aux événements pour module ${moduleId}`);
    
    // S'abonner aux événements d'évaluation
    const unsubscribeEval = subscribeToEvaluation((data) => {
      console.log(`[PedagogicalObjectives] 📡 Événement evaluation_complete reçu:`, data);
      // Recharger si c'est notre module ou si pas de module spécifié
      if (!data.module_id || data.module_id === parseInt(moduleId)) {
        console.log(`[PedagogicalObjectives] 🔄 Déclenchement refresh après évaluation...`);
        fetchProgressData(false); // Sans loader pour fluidité
      }
    });

    // S'abonner aux événements de refresh manuel
    const unsubscribeRefresh = subscribeToRefresh((data) => {
      console.log(`[PedagogicalObjectives] 📡 Événement refresh reçu:`, data);
      if (!data.moduleId || data.moduleId === parseInt(moduleId)) {
        fetchProgressData(false);
      }
    });

    // Cleanup à la destruction du composant
    return () => {
      console.log(`[PedagogicalObjectives] 🧹 Désabonnement événements module ${moduleId}`);
      unsubscribeEval();
      unsubscribeRefresh();
    };
  }, [moduleId, fetchProgressData]);

  const fetchProgress = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/pedagogical/progress/${moduleId}`);
      setProgressData(response.data);
      setError(null);
    } catch (err) {
      console.error('Erreur chargement progression pédagogique:', err);
      setError('Impossible de charger la progression');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card className="border border-[#FFD86F]/15 overflow-hidden bg-white/60 backdrop-blur-xl">
        <CardContent className="p-6">
          <div className="flex items-center justify-center space-x-3">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
              className="w-6 h-6 border-2 border-[#FFD86F] border-t-transparent rounded-full"
            />
            <span className="text-gray-600">Chargement des objectifs...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="border border-red-200 overflow-hidden bg-red-50/60">
        <CardContent className="p-6 text-center text-red-600">
          {error}
        </CardContent>
      </Card>
    );
  }

  const { 
    objectives_progress = [], 
    module_validated, 
    badge_unlocked,
    total_objectives,
    validated_objectives,
    total_evidence_count = 0,
    total_min_evidence_count = 0  // 🔒 Audit UI: Total preuves attendues
  } = progressData || {};

  // Calculer le pourcentage global du module
  const globalProgress = objectives_progress.length > 0
    ? objectives_progress.reduce((sum, obj) => sum + obj.progress_percentage, 0) / objectives_progress.length
    : 0;

  return (
    <motion.div
      initial={{ opacity: 0, x: 30 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.6, delay: 0.4 }}
      className="space-y-4"
    >
      {/* Carte Principale - Objectifs Pédagogiques */}
      <Card 
        className="border border-[#FFD86F]/15 overflow-hidden"
        style={{
          background: 'rgba(255, 255, 255, 0.7)',
          backdropFilter: 'blur(20px)',
          boxShadow: '0 0 40px rgba(255, 216, 111, 0.15)'
        }}
      >
        <CardHeader className="border-b border-[#FFD86F]/10 bg-gradient-to-r from-white/50 to-[#FFD86F]/5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-br from-[#3B1E73] to-[#8B5CF6] rounded-lg shadow-md">
                <Target className="w-6 h-6 text-white" />
              </div>
              <div>
                <CardTitle className="text-xl font-bold text-[#2B1854]">
                  Objectifs Pédagogiques
                </CardTitle>
                <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-1">
                  <Lock className="w-3 h-3" />
                  Évaluation exclusive par Owen
                </p>
              </div>
            </div>
            
            {/* Badge de validation globale */}
            <Badge 
              className={`px-3 py-1.5 rounded-full font-semibold ${
                module_validated 
                  ? 'bg-green-100 text-green-700 border-green-300' 
                  : 'bg-amber-50 text-amber-700 border-amber-200'
              }`}
            >
              {validated_objectives}/{total_objectives} validés
            </Badge>
          </div>
        </CardHeader>
        
        <CardContent className="p-6 space-y-5">
          {/* Liste des objectifs */}
          <div className="space-y-4">
            {objectives_progress.map((objective, index) => (
              <ObjectiveItem 
                key={objective.objective_id} 
                objective={objective} 
                index={index}
              />
            ))}
          </div>

          {/* Message Qualiopi */}
          <div className="flex items-start gap-3 p-4 rounded-xl bg-gradient-to-r from-[#3B1E73]/5 to-[#8B5CF6]/5 border border-[#3B1E73]/10">
            <Shield className="w-5 h-5 text-[#3B1E73] flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-[#3B1E73] font-medium">
                Évaluation certifiée Qualiopi
              </p>
              <p className="text-xs text-gray-600 mt-1">
                Vos compétences sont validées par Owen sur la base de preuves comportementales 
                observées lors de vos sessions d&apos;entraînement.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Carte Progression Module */}
      <Card 
        className="border border-[#FFD86F]/15 overflow-hidden"
        style={{
          background: 'rgba(255, 255, 255, 0.6)',
          backdropFilter: 'blur(16px)',
          boxShadow: '0 0 30px rgba(255, 216, 111, 0.15)'
        }}
      >
        <CardHeader className="border-b border-[#FFD86F]/10 bg-white/30 pb-3">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-br from-[#FFD86F] to-[#FFB347] rounded-lg shadow-md">
              <Award className="w-5 h-5 text-white" />
            </div>
            <CardTitle className="text-lg font-bold text-[#2B1854]">
              Progression du Module
            </CardTitle>
          </div>
        </CardHeader>
        
        <CardContent className="p-5 space-y-4">
          {/* Barre de progression globale */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600">Maîtrise globale</span>
              <span className="text-lg font-bold text-[#3B1E73]">
                {Math.round(globalProgress)}%
              </span>
            </div>
            <div className="relative h-3 bg-gray-100 rounded-full overflow-hidden">
              <motion.div
                className="absolute inset-y-0 left-0 bg-gradient-to-r from-[#FFD86F] via-[#FFB347] to-[#E879F9] rounded-full"
                initial={{ width: 0 }}
                animate={{ width: `${globalProgress}%` }}
                transition={{ duration: 1, delay: 0.3 }}
              />
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 rounded-xl bg-[#FFD86F]/10 border border-[#FFD86F]/20">
              <div className="flex items-center gap-2 mb-1">
                <Brain className="w-4 h-4 text-[#FFB347]" />
                <span className="text-xs text-gray-600">Preuves collectées</span>
              </div>
              {/* 🔒 Audit UI (30/12/2024): Affichage N / M preuves - RÈGLE: toujours /5, jamais /3 */}
              <span className="text-xl font-bold text-[#3B1E73]">
                {total_evidence_count} / {total_min_evidence_count || objectives_progress.length * 5}
              </span>
            </div>
            <div className="p-3 rounded-xl bg-[#8B5CF6]/10 border border-[#8B5CF6]/20">
              <div className="flex items-center gap-2 mb-1">
                <TrendingUp className="w-4 h-4 text-[#8B5CF6]" />
                <span className="text-xs text-gray-600">Objectifs validés</span>
              </div>
              <span className="text-xl font-bold text-[#3B1E73]">{validated_objectives}/{total_objectives}</span>
            </div>
          </div>

          {/* Statut Module */}
          <AnimatePresence>
            {module_validated ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.9, y: -10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ type: "spring", stiffness: 200, damping: 15 }}
                className="p-5 rounded-2xl border border-green-500/20 bg-gradient-to-br from-green-50 to-emerald-50"
                style={{ boxShadow: '0 0 20px rgba(34, 197, 94, 0.15)' }}
              >
                <div className="flex items-center gap-3 mb-3">
                  <motion.div
                    animate={{ rotate: [0, 10, -10, 0] }}
                    transition={{ duration: 0.5, repeat: 2 }}
                  >
                    <Award className="w-6 h-6 text-amber-500" />
                  </motion.div>
                  <span className="font-bold text-green-700 text-lg">
                    ✓ Module Validé par Owen
                  </span>
                </div>
                <p className="text-sm text-green-600 leading-relaxed">
                  Félicitations ! Vous avez démontré la maîtrise de tous les objectifs 
                  pédagogiques du Module {moduleOrder}. Badge Leadership débloqué.
                </p>
              </motion.div>
            ) : (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="p-4 rounded-xl bg-gray-50 border border-gray-200"
              >
                <div className="flex items-center gap-2 text-gray-600">
                  <Sparkles className="w-5 h-5 text-[#FFD86F]" />
                  <span className="text-sm">
                    Continuez vos entraînements pour faire progresser vos objectifs
                  </span>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </CardContent>
      </Card>

      {/* 🔒 MOA (Jan 2025): Section "Critères d'évaluation" SUPPRIMÉE
          Les critères sont maintenant intégrés directement dans chaque ObjectiveItem (accordion) */}

      {/* Badge Section (si débloqué) */}
      <AnimatePresence>
        {badge_unlocked && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ delay: 0.3 }}
          >
            <Card 
              className="border-2 border-amber-300 overflow-hidden"
              style={{
                background: 'linear-gradient(135deg, #FFF8E1 0%, #FFE082 100%)',
                boxShadow: '0 0 30px rgba(255, 193, 7, 0.3)'
              }}
            >
              <CardContent className="p-5">
                <div className="flex items-center gap-4">
                  <motion.div
                    animate={{ 
                      scale: [1, 1.1, 1],
                      rotate: [0, 5, -5, 0]
                    }}
                    transition={{ 
                      duration: 2, 
                      repeat: Infinity,
                      repeatType: "reverse"
                    }}
                    className="p-3 bg-gradient-to-br from-amber-400 to-amber-600 rounded-full shadow-lg"
                  >
                    <Award className="w-8 h-8 text-white" />
                  </motion.div>
                  <div>
                    <h4 className="font-bold text-amber-800 text-lg">
                      🏆 Badge Débloqué !
                    </h4>
                    <p className="text-amber-700 text-sm">
                      Leadership Module {moduleOrder} - Compétences certifiées par Owen
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

/**
 * 🎯 MOA (Jan 2025): Composant Objectif avec Critères Intégrés
 * Les critères sont affichés DIRECTEMENT sous chaque objectif (accordion)
 * Statuts visuels : 🟢 Démontré, 🟡 Partiel, ⚪ Non observé
 * LECTURE SEULE - Données certifiantes uniquement
 */
const ObjectiveItem = ({ objective, index }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  const { 
    objective_id, 
    title, 
    progress_percentage = 0, 
    validated = false,
    evidence_count = 0,
    min_evidence_count = 5,
    criteria = []  // 🔒 MOA: Critères avec statut certifiant
  } = objective;

  const isComplete = validated || progress_percentage >= 100;
  const isInProgress = progress_percentage > 0 && progress_percentage < 100;
  
  // Compter les critères par statut pour affichage rapide
  // Helper pour le style du statut d'un critère
  const getCriterionStatusDisplay = (status) => {
    switch (status) {
      case 'clearly_demonstrated':
        return {
          icon: <CheckCircle2 className="w-4 h-4" />,
          label: 'Démontré',
          bgColor: 'bg-green-50',
          borderColor: 'border-green-300',
          textColor: 'text-green-700',
          iconColor: 'text-green-500',
          badgeBg: 'bg-green-100'
        };
      case 'partially_demonstrated':
        return {
          icon: <AlertCircle className="w-4 h-4" />,
          label: 'Partiel',
          bgColor: 'bg-amber-50',
          borderColor: 'border-amber-300',
          textColor: 'text-amber-700',
          iconColor: 'text-amber-500',
          badgeBg: 'bg-amber-100'
        };
      default:
        return {
          icon: <Circle className="w-4 h-4" />,
          label: 'Non observé',
          bgColor: 'bg-gray-50',
          borderColor: 'border-gray-200',
          textColor: 'text-gray-500',
          iconColor: 'text-gray-400',
          badgeBg: 'bg-gray-100'
        };
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.1 }}
      className={`rounded-xl border overflow-hidden transition-all ${
        isComplete 
          ? 'bg-green-50/80 border-green-200' 
          : isInProgress 
            ? 'bg-amber-50/50 border-amber-200/50' 
            : 'bg-white/50 border-gray-200'
      }`}
      data-testid={`objective-card-${objective_id}`}
    >
      {/* === Header de l'objectif (cliquable pour accordion) === */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full p-4 flex items-start gap-3 hover:bg-white/30 transition-colors text-left"
        data-testid={`objective-toggle-${objective_id}`}
      >
        {/* Icône d'expansion */}
        <motion.div
          animate={{ rotate: isExpanded ? 90 : 0 }}
          transition={{ duration: 0.2 }}
          className="flex-shrink-0 mt-1"
        >
          <ChevronRight className="w-4 h-4 text-gray-400" />
        </motion.div>
        
        {/* Checkbox visuelle (LECTURE SEULE) */}
        <div className="flex-shrink-0 mt-0.5">
          {isComplete ? (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <CheckCircle2 className="w-6 h-6 text-green-500" />
            </motion.div>
          ) : (
            <Circle className={`w-6 h-6 ${isInProgress ? 'text-amber-400' : 'text-gray-300'}`} />
          )}
        </div>

        {/* Contenu principal */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <h4 className={`text-sm font-semibold leading-tight ${
              isComplete ? 'text-green-700' : 'text-[#2B1854]'
            }`}>
              {title}
            </h4>
            
            {/* 🔒 MOA (Jan 2026): Badges résumé critères SUPPRIMÉS - doublon UX */}
          </div>
          
          {/* Barre de progression */}
          <div className="mt-2">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-gray-500">
                {evidence_count} / {min_evidence_count} preuve{min_evidence_count > 1 ? 's' : ''}
              </span>
              <span className={`text-xs font-bold ${
                isComplete ? 'text-green-600' : isInProgress ? 'text-amber-600' : 'text-gray-400'
              }`}>
                {Math.round(progress_percentage)}%
              </span>
            </div>
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
              <motion.div
                className={`h-full rounded-full ${
                  isComplete 
                    ? 'bg-gradient-to-r from-green-400 to-green-500' 
                    : 'bg-gradient-to-r from-amber-400 to-amber-500'
                }`}
                initial={{ width: 0 }}
                animate={{ width: `${progress_percentage}%` }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
              />
            </div>
          </div>

          {/* Badge de statut objectif */}
          {isComplete && (
            <motion.div
              initial={{ opacity: 0, y: 5 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-2"
            >
              <Badge className="bg-green-100 text-green-700 border-green-200 text-xs">
                ✓ Validé par Owen
              </Badge>
            </motion.div>
          )}
        </div>
      </button>
      
      {/* === Section Critères (accordion dépliable) === */}
      <AnimatePresence>
        {isExpanded && criteria.length > 0 && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4 pt-1 border-t border-gray-100/50">
              <p className="text-xs text-gray-500 mb-2 font-medium">
                Critères d&apos;évaluation ({criteria.length})
              </p>
              <div className="space-y-2">
                {criteria.map((criterion, critIndex) => {
                  const statusDisplay = getCriterionStatusDisplay(criterion.status);
                  
                  return (
                    <motion.div
                      key={`${critIndex}-${criterion.status}`}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -10 }}
                      transition={{ delay: critIndex * 0.05 }}
                      className={`p-3 rounded-lg border ${statusDisplay.bgColor} ${statusDisplay.borderColor} transition-all`}
                      data-testid={`criterion-${objective_id}-${critIndex}`}
                    >
                      <div className="flex items-start gap-2.5">
                        {/* Icône du statut avec animation */}
                        <motion.div 
                          className={`flex-shrink-0 mt-0.5 ${statusDisplay.iconColor}`}
                          initial={{ scale: 0, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          transition={{ type: "spring", stiffness: 400, damping: 15 }}
                        >
                          {statusDisplay.icon}
                        </motion.div>
                        
                        {/* Contenu du critère */}
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm leading-relaxed ${statusDisplay.textColor}`}>
                            {criterion.criterion_text}
                          </p>
                          
                          {/* Badge statut + animation */}
                          <motion.span 
                            initial={{ opacity: 0, y: 5 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: critIndex * 0.05 + 0.1 }}
                            className={`inline-flex items-center gap-1 mt-1.5 px-2 py-0.5 text-xs font-medium rounded-full ${statusDisplay.badgeBg} ${statusDisplay.textColor}`}
                          >
                            {statusDisplay.label}
                          </motion.span>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Indicateur "cliquez pour voir les critères" si replié et critères existent */}
      {!isExpanded && criteria.length > 0 && (
        <div className="px-4 pb-3 pt-0">
          <p className="text-xs text-gray-400 flex items-center gap-1">
            <ChevronDown className="w-3 h-3" />
            Cliquez pour voir les {criteria.length} critères
          </p>
        </div>
      )}
    </motion.div>
  );
};

export default PedagogicalObjectives;
